import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// Styled components with enhanced design
const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  max-width: 100%;
  overflow: hidden;
`;

const ControlBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  
  svg {
    position: absolute;
    left: 10px;
    color: #6c757d;
    width: 16px;
    height: 16px;
  }

  input {
    width: 280px;
    padding: 0.625rem 0.625rem 0.625rem 2rem;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.875rem;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #4361ee;
      box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.2);
    }
    
    &::placeholder {
      color: #a0aec0;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    
    input {
      width: 100%;
    }
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #edf2f7;
    border-color: #cbd5e0;
  }
  
  &:active {
    background: #e2e8f0;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 0.75rem;
  background-color: #4361ee;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #3a56e4;
  }

  &:active {
    background-color: #2e46c9;
  }
`;

const StyledSelect = styled.select`
  padding: 0.375rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.75rem;
  background: white;
  color: #2d3748;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 200px;

  &:focus {
    outline: none;
    border-color: #4361ee;
    box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.15);
  }

  &:hover {
    border-color: #cbd5e0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const TableContainer = styled.div`
  position: relative;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  width: 100%;
  height: 600px;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const TableScroller = styled.div`
  overflow-x: auto;
  width: 100%;
  
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  position: relative;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  background: #f8fafc;
  z-index: 10;
  padding: 0.875rem 1rem;
  text-align: left;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
  min-width: 150px;
  font-weight: 600;
  color: #4a5568;
  font-size: 0.875rem;
  
  &:first-child {
    left: 0;
    z-index: 20;
  }
  
  div {
    display: flex;
    align-items: center;
  }
`;

const Td = styled.td`
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.875rem;
  color: #2d3748;
  transition: background 0.15s ease;

  &:first-child {
    position: sticky;
    left: 0;
    background: white;
    z-index: 5;
    font-weight: 500;
  }

  a {
    color: #4361ee;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
    
    &::after {
      content: '→';
      font-size: 0.875rem;
    }
  }
`;

const SortButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  margin-left: 0.25rem;
  cursor: pointer;
  color: #a0aec0;
  border-radius: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    color: #4a5568;
    background: #edf2f7;
  }
`;

const FilterInput = styled.input`
  width: calc(100% - 0.5rem);
  padding: 0.375rem 0.5rem;
  margin-top: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.75rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #4361ee;
    box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.15);
  }
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background 0.15s ease;
  
  &:hover {
    background: #f7fafc;
  }

  ${({ selected }) => selected && `
    background: #ebf4ff;
    td {
      color: #2b6cb0;
    }
    font-weight: 500;
  `}

  &:hover td:first-child {
    background: #f7fafc;
  }

  ${({ selected }) => selected && `
    & td:first-child {
      background: #ebf4ff;
    }
  `}
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: white;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 8px 8px;
  font-size: 0.875rem;
  color: #4a5568;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #a0aec0;
  text-align: center;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    color: #cbd5e0;
  }
  
  h3 {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: #4a5568;
  }
  
  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

// Icons with improved design
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="M21 21l-4.35-4.35"/>
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const NoDataIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    <line x1="2" y1="2" x2="22" y2="22"></line>
  </svg>
);

// Same function as before for CSV export
function filterRowsForCSV(rows) {
  const mutableData = JSON.parse(JSON.stringify(rows));
  
  for (let i = 0; i < mutableData.length; i++) {
    for (let j = 0; j < mutableData[i].length; j++) {
      if (mutableData[i][j] === undefined || mutableData[i][j] === null) {
        mutableData[i][j] = "";
        continue;
      }
      
      // Handle object cells (links and buttons)
      if (typeof mutableData[i][j] === 'object' && mutableData[i][j] !== null) {
        if (mutableData[i][j].link) {
          mutableData[i][j] = mutableData[i][j].value || "";
        } else if (mutableData[i][j].button) {
          mutableData[i][j] = ""; // Don't include button text in CSV
        } else if (mutableData[i][j].select) {
          mutableData[i][j] = mutableData[i][j].value || "";
        } else {
          mutableData[i][j] = JSON.stringify(mutableData[i][j]).replace(/"/g, '\'');
        }
      }
      
      // Convert to string if it isn't already
      mutableData[i][j] = String(mutableData[i][j]);
      
      // Clean up the string
      mutableData[i][j] = mutableData[i][j].replaceAll(';', ' ');
      mutableData[i][j] = mutableData[i][j].replaceAll('\n', ' ');
      mutableData[i][j] = mutableData[i][j].replaceAll('#', ' ');
      
      // eslint-disable-next-line no-control-regex
      mutableData[i][j] = mutableData[i][j].replace(/(\r\n|\n|\r|\u2028|\u000d|\u000a|\u2029)/gm, ' ');
      
      // Remove control characters - keeping the same regex as before
      // eslint-disable-next-line no-control-regex
      var re = /[\0-\x1F\x7F-\x9F\xAD\u0378\u0379\u037F-\u0383\u038B\u038D\u03A2\u0528-\u0530\u0557\u0558\u0560\u0588\u058B-\u058E\u0590\u05C8-\u05CF\u05EB-\u05EF\u05F5-\u0605\u061C\u061D\u06DD\u070E\u070F\u074B\u074C\u07B2-\u07BF\u07FB-\u07FF\u082E\u082F\u083F\u085C\u085D\u085F-\u089F\u08A1\u08AD-\u08E3\u08FF\u0978\u0980\u0984\u098D\u098E\u0991\u0992\u09A9\u09B1\u09B3-\u09B5\u09BA\u09BB\u09C5\u09C6\u09C9\u09CA\u09CF-\u09D6\u09D8-\u09DB\u09DE\u09E4\u09E5\u09FC-\u0A00\u0A04\u0A0B-\u0A0E\u0A11\u0A12\u0A29\u0A31\u0A34\u0A37\u0A3A\u0A3B\u0A3D\u0A43-\u0A46\u0A49\u0A4A\u0A4E-\u0A50\u0A52-\u0A58\u0A5D\u0A5F-\u0A65\u0A76-\u0A80\u0A84\u0A8E\u0A92\u0AA9\u0AB1\u0AB4\u0ABA\u0ABB\u0AC6\u0ACA\u0ACE\u0ACF\u0AD1-\u0ADF\u0AE4\u0AE5\u0AF2-\u0B00\u0B04\u0B0D\u0B0E\u0B11\u0B12\u0B29\u0B31\u0B34\u0B3A\u0B3B\u0B45\u0B46\u0B49\u0B4A\u0B4E-\u0B55\u0B58-\u0B5B\u0B5E\u0B64\u0B65\u0B78-\u0B81\u0B84\u0B8B-\u0B8D\u0B91\u0B96-\u0B98\u0B9B\u0B9D\u0BA0-\u0BA2\u0BA5-\u0BA7\u0BAB-\u0BAD\u0BBA-\u0BBD\u0BC3-\u0BC5\u0BC9\u0BCE\u0BCF\u0BD1-\u0BD6\u0BD8-\u0BE5\u0BFB-\u0C00\u0C04\u0C0D\u0C11\u0C29\u0C34\u0C3A-\u0C3C\u0C45\u0C49\u0C4E-\u0C54\u0C57\u0C5A-\u0C5F\u0C64\u0C65\u0C70-\u0C77\u0C80\u0C81\u0C84\u0C8D\u0C91\u0CA9\u0CB4\u0CBA\u0CBB\u0CC5\u0CC9\u0CCE-\u0CD4\u0CD7-\u0CDD\u0CDF\u0CE4\u0CE5\u0CF0\u0CF3-\u0D01\u0D04\u0D0D\u0D11\u0D3B\u0D3C\u0D45\u0D49\u0D4F-\u0D56\u0D58-\u0D5F\u0D64\u0D65\u0D76-\u0D78\u0D80\u0D81\u0D84\u0D97-\u0D99\u0DB2\u0DBC\u0DBE\u0DBF\u0DC7-\u0DC9\u0DCB-\u0DCE\u0DD5\u0DD7\u0DE0-\u0DF1\u0DF5-\u0E00\u0E3B-\u0E3E\u0E5C-\u0E80\u0E83\u0E85\u0E86\u0E89\u0E8B\u0E8C\u0E8E-\u0E93\u0E98\u0EA0\u0EA4\u0EA6\u0EA8\u0EA9\u0EAC\u0EBA\u0EBE\u0EBF\u0EC5\u0EC7\u0ECE\u0ECF\u0EDA\u0EDB\u0EE0-\u0EFF\u0F48\u0F6D-\u0F70\u0F98\u0FBD\u0FCD\u0FDB-\u0FFF\u10C6\u10C8-\u10CC\u10CE\u10CF\u1249\u124E\u124F\u1257\u1259\u125E\u125F\u1289\u128E\u128F\u12B1\u12B6\u12B7\u12BF\u12C1\u12C6\u12C7\u12D7\u1311\u1316\u1317\u135B\u135C\u137D-\u137F\u139A-\u139F\u13F5-\u13FF\u169D-\u169F\u16F1-\u16FF\u170D\u1715-\u171F\u1737-\u173F\u1754-\u175F\u176D\u1771\u1774-\u177F\u17DE\u17DF\u17EA-\u17EF\u17FA-\u17FF\u180F\u181A-\u181F\u1878-\u187F\u18AB-\u18AF\u18F6-\u18FF\u191D-\u191F\u192C-\u192F\u193C-\u193F\u1941-\u1943\u196E\u196F\u1975-\u197F\u19AC-\u19AF\u19CA-\u19CF\u19DB-\u19DD\u1A1C\u1A1D\u1A5F\u1A7D\u1A7E\u1A8A-\u1A8F\u1A9A-\u1A9F\u1AAE-\u1AFF\u1B4C-\u1B4F\u1B7D-\u1B7F\u1BF4-\u1BFB\u1C38-\u1C3A\u1C4A-\u1C4C\u1C80-\u1CBF\u1CC8-\u1CCF\u1CF7-\u1CFF\u1DE7-\u1DFB\u1F16\u1F17\u1F1E\u1F1F\u1F46\u1F47\u1F4E\u1F4F\u1F58\u1F5A\u1F5C\u1F5E\u1F7E\u1F7F\u1FB5\u1FC5\u1FD4\u1FD5\u1FDC\u1FF0\u1FF1\u1FF5\u1FFF\u200B-\u200F\u202A-\u202E\u2060-\u206F\u2072\u2073\u208F\u209D-\u209F\u20BB-\u20CF\u20F1-\u20FF\u218A-\u218F\u23F4-\u23FF\u2427-\u243F\u244B-\u245F\u2700\u2B4D-\u2B4F\u2B5A-\u2BFF\u2C2F\u2C5F\u2CF4-\u2CF8\u2D26\u2D28-\u2D2C\u2D2E\u2D2F\u2D68-\u2D6E\u2D71-\u2D7E\u2D97-\u2D9F\u2DA7\u2DAF\u2DB7\u2DBF\u2DC7\u2DCF\u2DD7\u2DDF\u2E3C-\u2E7F\u2E9A\u2EF4-\u2EFF\u2FD6-\u2FEF\u2FFC-\u2FFF\u3040\u3097\u3098\u3100-\u3104\u312E-\u3130\u318F\u31BB-\u31BF\u31E4-\u31EF\u321F\u32FF\u4DB6-\u4DBF\u9FCD-\u9FFF\uA48D-\uA48F\uA4C7-\uA4CF\uA62C-\uA63F\uA698-\uA69E\uA6F8-\uA6FF\uA78F\uA794-\uA79F\uA7AB-\uA7F7\uA82C-\uA82F\uA83A-\uA83F\uA878-\uA87F\uA8C5-\uA8CD\uA8DA-\uA8DF\uA8FC-\uA8FF\uA954-\uA95E\uA97D-\uA97F\uA9CE\uA9DA-\uA9DD\uA9E0-\uA9FF\uAA37-\uAA3F\uAA4E\uAA4F\uAA5A\uAA5B\uAA7C-\uAA7F\uAAC3-\uAADA\uAAF7-\uAB00\uAB07\uAB08\uAB0F\uAB10\uAB17-\uAB1F\uAB27\uAB2F-\uABBF\uABEE\uABEF\uABFA-\uABFF\uD7A4-\uD7AF\uD7C7-\uD7CA\uD7FC-\uF8FF\uFA6E\uFA6F\uFADA-\uFAFF\uFB07-\uFB12\uFB18-\uFB1C\uFB37\uFB3D\uFB3F\uFB42\uFB45\uFBC2-\uFBD2\uFD40-\uFD4F\uFD90\uFD91\uFDC8-\uFDEF\uFDFE\uFDFF\uFE1A-\uFE1F\uFE27-\uFE2F\uFE53\uFE67\uFE6C-\uFE6F\uFE75\uFEFD-\uFF00\uFFBF-\uFFC1\uFFC8\uFFC9\uFFD0\uFFD1\uFFD8\uFFD9\uFFDD-\uFFDF\uFFE7\uFFEF-\uFFFB\uFFFE\uFFFF]/g;
      mutableData[i][j] = mutableData[i][j].replace(re, "");
      
      // Wrap in quotes and escape existing quotes
      mutableData[i][j] = '"' + mutableData[i][j].replaceAll('"', '\'') + '"';
    }
  }
  
  return mutableData;
}

const EnhancedTable = ({ data, sortByColumnIndex = 0, onButtonClick, onSelectChange, dataType = 'data' }) => {
  const [filteredData, setFilteredData] = useState(data);
  const [columnToSortIndex, setColumnToSortIndex] = useState(sortByColumnIndex);
  const [sortDirection, setSortDirection] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);

  // Debounce search term (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce column filters (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const handleRowClick = (index) => {
    setSelectedRow(index === selectedRow ? null : index);
  };

  const linkColumns = [
    "Lien vers le profil",
    "Lien pour éditer le profil",
    "Lien vers le profil HubSpot",
    "Photo de courverture",
    "Photo de profil",
    "CV",
    "Lien linkedin",
    "Lien vers la société",
    "Lien pour éditer la société",
    "Lien vers la société HubSpot",
    "Lien vers le projet",
    "Lien pour éditer le projet"
  ];

  const actionsColumn = "Actions";

  // Export to CSV function
  const exportToCSV = () => {
    // Get filename based on data type
    let filename;
    switch(dataType) {
      case 'profiles':
        filename = 'profiles.csv';
        break;
      case 'companies':
        filename = 'societes.csv';
        break;
      case 'projects':
        filename = 'projets.csv';
        break;
      default:
        filename = 'export.csv';
    }
    
    // Process the data for CSV
    const processedData = filterRowsForCSV(data);
    const csvContent = "data:text/csv;charset=utf-8," + processedData.map(row => row.join(";")).join("\n");
    
    // Create and trigger download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    let result = [...data].slice(1);

    // Apply search term filter (using debounced value)
    if (debouncedSearchTerm) {
      const lower = debouncedSearchTerm.toLowerCase();
      result = result.filter(row =>
        row.some(cell =>
          cellValue(cell).toLowerCase().includes(lower)
        )
      );
    }

    // Apply column-specific filters (using debounced values)
    Object.entries(debouncedFilters).forEach(([columnIndex, filterValue]) => {
      if (filterValue) {
        const lower = filterValue.toLowerCase();
        result = result.filter(row =>
          cellValue(row[columnIndex]).toLowerCase().includes(lower)
        );
      }
    });

    // Apply sorting
    result.sort((a, b) => {
      if (!a[columnToSortIndex]) return 1 * sortDirection;
      if (!b[columnToSortIndex]) return -1 * sortDirection;
      return cellValue(a[columnToSortIndex]).localeCompare(cellValue(b[columnToSortIndex])) * sortDirection;
    });

    // Add header row back
    result.unshift(data[0]);
    setFilteredData(result);
  }, [data, debouncedSearchTerm, debouncedFilters, columnToSortIndex, sortDirection]);

  // Helper to extract value from cell (object or primitive)
  const cellValue = (cell) => {
    if (!cell) return "";
    if (typeof cell === 'object' && cell.link === true) return cell.value || "";
    if (typeof cell === 'object' && cell.button === true) return cell.label || "";
    if (typeof cell === 'object' && cell.select === true) return cell.value || "";
    return String(cell);
  };

  // Helper to render cell content with appropriate formatting
  const cellObjectToHTML = (cell, columnName) => {
    if (!cell) return "";
    
    if (typeof cell === 'object') {
      if (cell.link === true) {
        return (
          <a href={cell.value} target="_blank" rel="noreferrer">
            Voir
          </a>
        );
      }
      
      if (cell.button === true) {
        return (
          <ActionButton
            onClick={(e) => {
              e.stopPropagation(); // Prevent row selection when clicking the button
              if (onButtonClick) onButtonClick(cell.onClick);
            }}
          >
            {cell.label}
          </ActionButton>
        );
      }

      if (cell.select === true) {
        return (
          <StyledSelect
            value={cell.value || ''}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              if (onSelectChange) onSelectChange(cell.onChange, e.target.value);
            }}
          >
            <option value="">—</option>
            {(cell.options || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </StyledSelect>
        );
      }
    }
    
    return cell;
  };

  // Handle sorting when user clicks a column header
  const handleSort = (index) => {
    if (columnToSortIndex === index) {
      setSortDirection(sortDirection * -1);
    } else {
      setColumnToSortIndex(index);
      setSortDirection(1);
    }
  };

  // Show empty state if no data
  if (!data || data.length <= 1) {
    return (
      <TableWrapper>
        <TableContainer>
          <EmptyState>
            <NoDataIcon />
            <h3>Aucune donnée disponible</h3>
            <p>Il n'y a aucun élément à afficher pour le moment.</p>
          </EmptyState>
        </TableContainer>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper>
      <ControlBar>
        <SearchBox>
          <SearchIcon />
          <input
            type="text"
            placeholder="Rechercher dans tous les champs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBox>
        <ButtonGroup>
          <Button onClick={exportToCSV}>
            <DownloadIcon />
            <span>Exporter CSV</span>
          </Button>
        </ButtonGroup>
      </ControlBar>

      <TableContainer>
        <TableScroller>
          <Table>
            <thead>
              <tr>
                {filteredData[0].map((cell, index) => (
                  <Th key={index}>
                    <div>
                      {cell}
                      <SortButton 
                        onClick={() => handleSort(index)}
                        title="Trier la colonne"
                      >
                        {columnToSortIndex === index ? (
                          sortDirection === 1 ? '↑' : '↓'
                        ) : '↕'}
                      </SortButton>
                    </div>
                    {!linkColumns.includes(cell) && cell !== actionsColumn && (
                      <FilterInput
                        type="text"
                        placeholder="Filtrer..."
                        onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking the filter
                        onChange={(e) => setFilters(prev => ({
                          ...prev,
                          [index]: e.target.value
                        }))}
                        value={filters[index] || ''}
                      />
                    )}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.slice(1).map((row, rowIndex) => (
                <Tr 
                  key={rowIndex} 
                  selected={selectedRow === rowIndex} 
                  onClick={() => handleRowClick(rowIndex)}
                >
                  {row.map((cell, cellIndex) => (
                    <Td key={cellIndex}>
                      {cellObjectToHTML(cell, filteredData[0][cellIndex])}
                    </Td>
                  ))}
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroller>
      </TableContainer>

      <Footer>
        <span>
          Affichage de {filteredData.length - 1} sur {data.length - 1} entrées
        </span>
      </Footer>
    </TableWrapper>
  );
};

export default EnhancedTable;